/* eslint-disable react-native/no-inline-styles */
// libraries
import React, {useState} from 'react';
import {useController} from 'react-hook-form';
import MaskedInput, {Masks} from 'react-native-mask-input';
import { Feather, MaterialCommunityIcons } from '@expo/vector-icons';

// js
import * as S from './styles';
import theme from '../../global/styles/theme';
import {maskInput, MaskInputProps} from './consts';
import TextDefault from '../TextDefault';
import {View} from 'react-native';
import {useSelector} from 'react-redux';
import {RootState} from '../../store';

// interfaces
interface Props {
  title: string;
  placeholder: string;
  name: string;
  control?: any;
  errors?: any;
  initialValue?: any;
  width?: string;
  keyboardType?: any;
  type?: 'password';
  height?: number;
  marginTop?: number;
  multiline?: boolean;
  disabled?: boolean;
  Icon?: any;
  hideBorder?: boolean;
  mask?: MaskInputProps;
  isDropdown?: boolean;
  options?: Array<{
    label: string;
    value: string | number;
  }>;
  maxLength?: number;
}

function Input(props: Props) {
  const {
    title,
    placeholder,
    name,
    control,
    errors,
    initialValue,
    width,
    keyboardType,
    type,
    height,
    marginTop,
    disabled,
    multiline,
    Icon,
    hideBorder,
    mask,
    isDropdown,
    options,
    maxLength,
    ...rest
  } = props;
  const defaultValue = initialValue || '';
  const controllers = {control, defaultValue, name};
  const {
    field: {value, onChange},
  } = useController(controllers);
  const appTheme = useSelector((state: RootState) => state.config.appTheme);

  const [viewPassword, setViewPassword] = useState(type === 'password');
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const chandeViewPassword = () => {
    setViewPassword(!viewPassword);
  };

  function maskChange(text: any): any {
    const currentMask = maskInput(text, mask);
    return currentMask;
  }

  function renderErrorMessage() {
    if (errors[name]?.message) {
      return (
        <S.ErrorText appTheme={appTheme} hideBorder={hideBorder}>
          {errors[name]?.message}
        </S.ErrorText>
      );
    }

    return null;
  }

  const handleDropdownPress = () => {
    setDropdownVisible(!dropdownVisible);
  };

  function RenderDropdownValues() {
    return options?.map(optionsValue => {
      const handleOptionPress = () => {
        setDropdownVisible(false);
        onChange(optionsValue.value);
      };

      return (
        <S.DropdownOptions key={optionsValue.value} onPress={handleOptionPress}>
          <TextDefault
            text={optionsValue.label}
            color={theme.colors(appTheme).white}
          />
        </S.DropdownOptions>
      );
    });
  }

  function renderInput() {
    if (isDropdown) {
      const dropdownValueName = value
        ? options?.find(optionsValue => optionsValue.value === value)?.label
        : placeholder;

      return (
        <S.DropdownContainer appTheme={appTheme}>
          <S.DropdownArea
            appTheme={appTheme}
            onPress={handleDropdownPress}
            style={{
              shadowColor: theme.colors(appTheme).black[700],
              shadowOffset: {width: 0, height: 2},
              shadowRadius: 4,
              shadowOpacity: 0.1,
              elevation: 2,
            }}
            height={height}
            hideBorder={hideBorder}>
            <View style={{flexDirection: 'row'}}>
              {Icon && Icon}
              <TextDefault
                style={{fontSize: 14, marginLeft: Icon ? 10 : 0}}
                text={dropdownValueName}
                color={theme.colors(appTheme).white}
              />
            </View>
            <Feather
              name={dropdownVisible ? 'chevron-up' : 'chevron-down'}
              size={24}
              color={theme.colors(appTheme).white}
            />
          </S.DropdownArea>
          {dropdownVisible && (
            <S.DropdownListArea appTheme={appTheme}>
              <S.DropdownList nestedScrollEnabled>
                {RenderDropdownValues()}
              </S.DropdownList>
            </S.DropdownListArea>
          )}
        </S.DropdownContainer>
      );
    }

    if (mask) {
      return (
        <S.InputArea
          height={height}
          hideBorder={hideBorder}
          appTheme={appTheme}
          style={{
            shadowColor: theme.colors(appTheme).black[700],
            shadowOffset: {width: 0, height: 2},
            shadowRadius: 4,
            shadowOpacity: 0.1,
            elevation: 2,
          }}>
          {Icon && Icon}
          <MaskedInput
            value={String(value)}
            onChangeText={onChange}
            mask={mask === 'money' ? Masks.BRL_CURRENCY : maskChange}
            placeholder={placeholder}
            keyboardType={keyboardType || 'default'}
            style={{
              width: Icon ? '90%' : '100%',
              marginLeft: Icon ? 10 : 0,
              paddingBottom: 0,
              paddingTop: 4,
              fontFamily: theme.fonts.family.opensans.regular,
              height: height || 50,
              fontSize: 14,
              color: theme.colors(appTheme).white,
              padding: 0,
            }}
            editable={!disabled}
            autoCapitalize="characters"
            placeholderTextColor={theme.colors(appTheme).white}
            maxLength={maxLength}
            selectionColor={theme.colors(appTheme).main[700]}
          />
        </S.InputArea>
      );
    }
    return (
      <S.InputArea
        height={height}
        appTheme={appTheme}
        hideBorder={hideBorder}
        style={{
          shadowColor: theme.colors(appTheme).black[700],
          shadowOffset: {width: 0, height: 2},
          shadowRadius: 4,
          shadowOpacity: 0.1,
          elevation: 2,
        }}>
        {Icon && Icon}
        <S.TextInput
          {...rest}
          appTheme={appTheme}
          placeholder={placeholder}
          value={value}
          onChangeText={onChange}
          placeholderTextColor={theme.colors(appTheme).white}
          keyboardType={keyboardType || 'default'}
          secureTextEntry={viewPassword}
          multiline={multiline}
          height={height}
          textAlignVertical={multiline ? 'top' : 'center'}
          Icon={Icon}
          editable={!disabled}
          maxLength={maxLength}
          selectionColor={theme.colors(appTheme).main[700]}
        />
        {type === 'password' && (
          <S.HideButton onPress={chandeViewPassword}>
            <MaterialCommunityIcons
              name={viewPassword ? 'eye-off' : 'eye'}
              color={theme.colors(appTheme).gray[400]}
              size={18}
            />
          </S.HideButton>
        )}
      </S.InputArea>
    );
  }

  return (
    <S.Container
      appTheme={appTheme}
      height={height}
      width={width}
      marginTop={marginTop}>
      {title ? (
        <S.Text
          appTheme={appTheme}
          font={theme.fonts.family.opensans.semi_bold}
          color={theme.colors(appTheme).white}>
          {title}
        </S.Text>
      ) : null}
      {renderInput()}
      {renderErrorMessage()}
    </S.Container>
  );
}

export default Input;
